import type { FrontendSettings, FrontendSettingsKey } from '@/types/userSettings';

/**
 * A key/value store for Dugout preferences, backed by the server and cached in
 * localStorage.
 *
 * It is a module singleton rather than a React context on purpose. Theme is
 * applied to `document.body` from a synchronous read during the first render,
 * and `Profile` and `Timeline` seed `useState` synchronously too, so the store
 * has to be readable before any provider has mounted. A singleton also means a
 * consumer's position in the tree doesn't matter.
 *
 * The flow is cache-first:
 *   1. Reads are served synchronously from localStorage, so nothing flashes.
 *   2. Server settings arrive later and win, writing through to the cache.
 *   3. Local changes apply immediately and are pushed to the server debounced.
 *
 * Values are layered. A staff member's own value always wins; underneath it
 * sits the default saved for their role, and underneath that whatever default
 * the calling component passes. Clearing a personal value therefore drops the
 * reader back onto their role's default rather than onto nothing.
 *
 * The localStorage keys and value formats are identical to the ones each
 * setting used before it moved here, so nothing needs migrating and an
 * unmigrated consumer keeps working.
 */

type Listener = () => void;

const listeners = new Set<Listener>();

/**
 * Tolerant of both formats we have on disk: values written as JSON, and the
 * bare strings that `theme` and `darkStyle` have always been stored as.
 */
const readCached = (key: FrontendSettingsKey): unknown => {
  let raw: string | null = null;

  try {
    raw = localStorage.getItem(key);
  } catch {
    // Private browsing and embedded contexts can throw on access.
    return undefined;
  }

  if (raw === null) return undefined;

  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
};

const writeCached = (key: FrontendSettingsKey, value: unknown) => {
  try {
    if (value === undefined || value === null) {
      localStorage.removeItem(key);
      return;
    }

    // Strings stay bare so the on-disk format matches what `theme` and
    // `darkStyle` have always written.
    localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
  } catch {
    // Losing the cache is survivable; the server still has the value.
  }
};

const SETTING_KEYS: FrontendSettingsKey[] = [
  'theme',
  'darkStyle',
  'sidebarCollapsed',
  'encounterSections',
  'profileSections',
  'timelineFilters',
  'navAnchorsCollapsed',
  'betaFeatures'
];

const readAllCached = (): FrontendSettings => {
  const settings: Record<string, unknown> = {};

  SETTING_KEYS.forEach((key) => {
    const value = readCached(key);
    if (value !== undefined) settings[key] = value;
  });

  return settings as FrontendSettings;
};

/** Role defaults are one bucket, so they cache as one JSON blob. */
const ROLE_DEFAULTS_CACHE_KEY = 'roleSettingDefaults';

const readCachedRoleDefaults = (): FrontendSettings => {
  try {
    const raw = localStorage.getItem(ROLE_DEFAULTS_CACHE_KEY);
    return raw ? (JSON.parse(raw) as FrontendSettings) : {};
  } catch {
    return {};
  }
};

let settings: FrontendSettings = readAllCached();

/** Defaults saved for the reader's role; every personal value outranks them. */
let roleDefaults: FrontendSettings = readCachedRoleDefaults();

const emit = () => listeners.forEach((listener) => listener());

export const subscribe = (listener: Listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * The reader's own value, ignoring any role default underneath it.
 *
 * Reads through to the cache for a key we haven't seen, so a value written
 * straight to localStorage (a consumer not yet migrated onto this store, or a
 * legacy key folded in after load) is still visible here.
 */
export const getPersonalSetting = <K extends FrontendSettingsKey>(key: K): FrontendSettings[K] => {
  if (key in settings) return settings[key];

  const cached = readCached(key);
  if (cached === undefined) return undefined;

  // Memoized into the map rather than re-parsed on every call. useSyncExternalStore
  // compares snapshots by reference, so handing back a freshly parsed object each
  // time would look like an endless stream of changes. No emit: nothing changed,
  // we just hadn't looked yet.
  settings = { ...settings, [key]: cached };

  return settings[key];
};

export const getSetting = <K extends FrontendSettingsKey>(key: K): FrontendSettings[K] => {
  const personal = getPersonalSetting(key);

  return personal === undefined ? roleDefaults[key] : personal;
};

export const getAllSettings = (): FrontendSettings => settings;

export const getRoleDefaults = (): FrontendSettings => roleDefaults;

/**
 * Installs the defaults saved for the reader's role.
 *
 * Cached like the settings themselves so a reader who has never overridden a
 * defaulted setting still gets their role's layout on the first render, before
 * the role settings request comes back.
 */
export const hydrateRoleDefaults = (next: FrontendSettings) => {
  if (isUnchanged(roleDefaults, next)) return;

  roleDefaults = next;

  try {
    localStorage.setItem(ROLE_DEFAULTS_CACHE_KEY, JSON.stringify(next));
  } catch {
    // Losing the cache is survivable; the server still has the defaults.
  }

  emit();
};

/** Pending local changes waiting to be flushed to the server. */
let pending: FrontendSettings = {};

export const takePending = (): FrontendSettings => {
  const taken = pending;
  pending = {};
  return taken;
};

export const hasPending = () => Object.keys(pending).length > 0;

/** Puts changes back after a failed flush, without clobbering newer edits. */
export const restorePending = (failed: FrontendSettings) => {
  pending = { ...failed, ...pending };
};

/**
 * Empties the cache when the user signs out.
 *
 * The bucket is per user on the server, but the cache is one set of
 * localStorage keys shared by whoever uses the browser. Without this the next
 * person to sign in on the same machine would render on the previous user's
 * theme and could write those values into their own bucket.
 */
export const clearSettings = () => {
  SETTING_KEYS.forEach((key) => writeCached(key, undefined));

  settings = {};
  pending = {};

  // The defaults underneath belong to the role the last reader worked in, so
  // they go with everything else rather than showing through to the next one.
  roleDefaults = {};

  try {
    localStorage.removeItem(ROLE_DEFAULTS_CACHE_KEY);
  } catch {
    // Losing the cache is survivable; the server still has the defaults.
  }

  emit();
};

let onLocalChange: (() => void) | null = null;

/** Registered by the sync hook so a local write can schedule a server flush. */
export const setChangeHandler = (handler: (() => void) | null) => {
  onLocalChange = handler;
};

/**
 * Settings are plain JSON, so value equality is the right test.
 *
 * Reference equality is not: Profile and Timeline rebuild their section list
 * and filter object on every mount, so an identical value would look like a
 * change and push a redundant write to the server on every navigation.
 */
const isUnchanged = (a: unknown, b: unknown) => {
  if (a === b) return true;
  if (a === undefined || b === undefined) return false;

  return JSON.stringify(a) === JSON.stringify(b);
};

export const setSetting = <K extends FrontendSettingsKey>(key: K, value: FrontendSettings[K]) => {
  if (isUnchanged(getSetting(key), value)) return;

  settings = { ...settings, [key]: value };
  pending = { ...pending, [key]: value ?? null };
  writeCached(key, value);
  emit();
  onLocalChange?.();
};

/**
 * Drops a personal value so the reader falls back to their role's default.
 *
 * The pending write is a null, which is how the backend deletes a key, so the
 * fallback follows them to their next session and their other tabs.
 */
export const clearSetting = (key: FrontendSettingsKey) => setSetting(key, undefined);

/**
 * Applies the server's copy on top of what we rendered from cache. The server
 * is authoritative, but a setting the user changed while the fetch was in
 * flight is still pending, so it must not be overwritten.
 */
export const hydrateFromServer = (serverSettings: FrontendSettings) => {
  const next = { ...settings, ...serverSettings, ...pending };

  SETTING_KEYS.forEach((key) => {
    if (!isUnchanged(next[key], settings[key])) writeCached(key, next[key]);
  });

  settings = next;
  emit();
};

/** Test seam: resets the module singleton between tests. */
export const resetForTests = () => {
  settings = readAllCached();
  roleDefaults = readCachedRoleDefaults();
  pending = {};
  listeners.clear();
  onLocalChange = null;
};
